import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/admin/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/customers/${editing.id}`, form);
      toast.success("Cliente atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/customers/${confirmDelete.id}`);
      toast.success("Cliente excluído. Histórico de pedidos preservado.");
      setConfirmDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Clientes</h1>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr><th className="p-3">Nome</th><th className="p-3">E-mail</th><th className="p-3">Telefone</th><th className="p-3">Cadastro</th><th className="p-3">Ações</th></tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-t" data-testid={`customer-row-${c.id}`}>
                <td className="p-3">{c.name}</td>
                <td className="p-3">{c.email}</td>
                <td className="p-3">{c.phone || "-"}</td>
                <td className="p-3">{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`edit-customer-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(c)} data-testid={`delete-customer-${c.id}`} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-neutral-500">Nenhum cliente cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar cliente</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="edit-customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><Label>E-mail</Label><Input data-testid="edit-customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><Label>Telefone</Label><Input data-testid="edit-customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <p className="text-xs text-neutral-500">A senha do cliente nunca é exibida ou editada aqui. Para redefinir, oriente o cliente a usar “Esqueci minha senha”.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" data-testid="save-customer-btn" className="bg-[#C91F1F] hover:bg-[#9F1515]">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              A ação removerá <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email}). O histórico de pedidos será preservado (o cliente será marcado como desvinculado). Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer" onClick={del} className="bg-[#C91F1F] hover:bg-[#9F1515]">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
